// Copyright (c) 2023 OPEN CASCADE SAS
//
// This file is part of commercial software by OPEN CASCADE SAS,
// furnished in accordance with the terms and conditions of the contract
// and with the inclusion of this copyright notice.
// This file or any part thereof may not be provided or otherwise
// made available to any third party.
//
// No ownership title to the software is transferred hereby.
//
// OPEN CASCADE SAS makes no representation or warranties with respect to the
// performance of this software, and specifically disclaims any responsibility
// for any damages, special or consequential, connected with its use.
import { OccViewer } from "../viewer/viewer.js";
import { Vec3Tools } from "./vec3-tools.js";
/**
 * Euler sequence.
 * represents gp_EulerSequence from OpenCascade.
 */
export var EulerSequence;
(function (EulerSequence) {
    //! Classic Euler angles, alias to Intrinsic_ZXZ
    EulerSequence[EulerSequence["gp_EulerAngles"] = 0] = "gp_EulerAngles";
    //! Yaw Pitch Roll (or nautical) angles, alias to Intrinsic_ZYX
    EulerSequence[EulerSequence["gp_YawPitchRoll"] = 1] = "gp_YawPitchRoll";
    // Tait-Bryan angles (using three different axes)
    EulerSequence[EulerSequence["gp_Extrinsic_XYZ"] = 2] = "gp_Extrinsic_XYZ";
    EulerSequence[EulerSequence["gp_Extrinsic_XZY"] = 3] = "gp_Extrinsic_XZY";
    EulerSequence[EulerSequence["gp_Extrinsic_YZX"] = 4] = "gp_Extrinsic_YZX";
    EulerSequence[EulerSequence["gp_Extrinsic_YXZ"] = 5] = "gp_Extrinsic_YXZ";
    EulerSequence[EulerSequence["gp_Extrinsic_ZXY"] = 6] = "gp_Extrinsic_ZXY";
    EulerSequence[EulerSequence["gp_Extrinsic_ZYX"] = 7] = "gp_Extrinsic_ZYX";
    EulerSequence[EulerSequence["gp_Intrinsic_XYZ"] = 8] = "gp_Intrinsic_XYZ";
    EulerSequence[EulerSequence["gp_Intrinsic_XZY"] = 9] = "gp_Intrinsic_XZY";
    EulerSequence[EulerSequence["gp_Intrinsic_YZX"] = 10] = "gp_Intrinsic_YZX";
    EulerSequence[EulerSequence["gp_Intrinsic_YXZ"] = 11] = "gp_Intrinsic_YXZ";
    EulerSequence[EulerSequence["gp_Intrinsic_ZXY"] = 12] = "gp_Intrinsic_ZXY";
    EulerSequence[EulerSequence["gp_Intrinsic_ZYX"] = 13] = "gp_Intrinsic_ZYX";
    // Proper Euler angles (using two different axes, first and third the same)
    EulerSequence[EulerSequence["gp_Extrinsic_XYX"] = 14] = "gp_Extrinsic_XYX";
    EulerSequence[EulerSequence["gp_Extrinsic_XZX"] = 15] = "gp_Extrinsic_XZX";
    EulerSequence[EulerSequence["gp_Extrinsic_YZY"] = 16] = "gp_Extrinsic_YZY";
    EulerSequence[EulerSequence["gp_Extrinsic_YXY"] = 17] = "gp_Extrinsic_YXY";
    EulerSequence[EulerSequence["gp_Extrinsic_ZYZ"] = 18] = "gp_Extrinsic_ZYZ";
    EulerSequence[EulerSequence["gp_Extrinsic_ZXZ"] = 19] = "gp_Extrinsic_ZXZ";
    EulerSequence[EulerSequence["gp_Intrinsic_XYX"] = 20] = "gp_Intrinsic_XYX";
    EulerSequence[EulerSequence["gp_Intrinsic_XZX"] = 21] = "gp_Intrinsic_XZX";
    EulerSequence[EulerSequence["gp_Intrinsic_YZY"] = 22] = "gp_Intrinsic_YZY";
    EulerSequence[EulerSequence["gp_Intrinsic_YXY"] = 23] = "gp_Intrinsic_YXY";
    EulerSequence[EulerSequence["gp_Intrinsic_ZXZ"] = 24] = "gp_Intrinsic_ZXZ";
    EulerSequence[EulerSequence["gp_Intrinsic_ZYZ"] = 25] = "gp_Intrinsic_ZYZ";
})(EulerSequence || (EulerSequence = {}));
/**
 * Auxiliary tool for math operations between 4D matrices.
 */
export class Mat4Tools {
    /** Return identity matrix. */
    static identity() {
        return [[1.0, 0.0, 0.0, 0.0], [0.0, 1.0, 0.0, 0.0], [0.0, 0.0, 1.0, 0.0], [0.0, 0.0, 0.0, 1.0]];
    }
    /** Return TRUE if matrix contains valid transformation. */
    static isValid(theMat) {
        if (!theMat || theMat.length !== 4) {
            return false;
        }
        let aZeroCounter = 0;
        for (let aRow = 0; aRow < 4; aRow++) {
            for (let aCol = 0; aCol < 4; aCol++) {
                const aVal = theMat[aCol][aRow];
                if (typeof (aVal) !== 'number' || Vec3Tools.isInfinite(aVal)) {
                    return false;
                }
                if (aVal === 0) {
                    aZeroCounter++;
                }
            }
        }
        return aZeroCounter <= 12;
    }
    /** Return transposed matrix. */
    static transpose(theMat) {
        const aResult = Mat4Tools.identity();
        for (let aRow = 0; aRow < 4; aRow++) {
            for (let aCol = 0; aCol < 4; aCol++) {
                aResult[aRow][aCol] = theMat[aCol][aRow];
            }
        }
        return aResult;
    }
    /** Compute per-component addition between two matrices. */
    static add(theMatA, theMatB) {
        const aResult = Mat4Tools.identity();
        for (let aRow = 0; aRow < theMatA.length; aRow++) {
            for (let aCol = 0; aCol < theMatA[0].length; aCol++) {
                aResult[aRow][aCol] = (theMatA[aRow][aCol] + theMatB[aRow][aCol]);
            }
        }
        return aResult;
    }
    /** Compute per-component subtraction between two matrices. */
    static subtract(theMatA, theMatB) {
        const aResult = Mat4Tools.identity();
        for (let aRow = 0; aRow < theMatA.length; aRow++) {
            for (let aCol = 0; aCol < theMatA[0].length; aCol++) {
                aResult[aRow][aCol] = (theMatA[aRow][aCol] - theMatB[aRow][aCol]);
            }
        }
        return aResult;
    }
    /** Compute per-component multiplication between two matrices. */
    static multiply(theMatA, theMatB) {
        if (typeof (theMatB) === 'number') {
            const aResult = Mat4Tools.identity();
            for (let aRow = 0; aRow < theMatA.length; aRow++) {
                for (let aCol = 0; aCol < theMatA[0].length; aCol++) {
                    aResult[aRow][aCol] = theMatA[aRow][aCol] * theMatB;
                }
            }
            return aResult;
        }
        else {
            return Mat4Tools._myViewer.mat4ToolsMultiply(theMatA, theMatB);
        }
    }
    /** Compute per-component division between two matrices. */
    static divide(theMat, theScalar) {
        const aResult = Mat4Tools.identity();
        for (let aRow = 0; aRow < theMat.length; aRow++) {
            for (let aCol = 0; aCol < theMat[0].length; aCol++) {
                aResult[aRow][aCol] = theMat[aRow][aCol] / theScalar;
            }
        }
        return aResult;
    }
    /** Compute determinant */
    static determinant(mat) {
        const [[a, b, c, d], [e, f, g, h], [i, j, k, l], [m, n, o, p]] = mat;
        return (a * this.determinant3x3([[f, g, h], [j, k, l], [n, o, p]])
            - b * this.determinant3x3([[e, g, h], [i, k, l], [m, o, p]])
            + c * this.determinant3x3([[e, f, h], [i, j, l], [m, n, p]])
            - d * this.determinant3x3([[e, f, g], [i, j, k], [m, n, o]]));
    }
    /** Compute determinant of 3x3 matrix */
    static determinant3x3(mat) {
        const [row1, row2, row3] = mat;
        return (row1[0] * (row2[1] * row3[2] - row2[2] * row3[1])
            - row1[1] * (row2[0] * row3[2] - row2[2] * row3[0])
            + row1[2] * (row2[0] * row3[1] - row2[1] * row3[0]));
    }
    /** Invert the matrix. */
    static invert(originalMatrix) {
        return Mat4Tools._myViewer.mat4ToolsInvert(originalMatrix);
    }
    /** Return translation part of matrix. */
    static getTranslation(theMat) {
        return { x: theMat[0][3], y: theMat[1][3], z: theMat[2][3] };
    }
    /** Return translation matrix. */
    static translation(theVector) {
        return [
            [1.0, 0.0, 0.0, theVector.x],
            [0.0, 1.0, 0.0, theVector.y],
            [0.0, 0.0, 1.0, theVector.z],
            [0.0, 0.0, 0.0, 1.0]
        ];
    }
    /** change translation part of matrix */
    static setTranslation(theMat, theVector) {
        theMat[0][3] = theVector.x;
        theMat[1][3] = theVector.y;
        theMat[2][3] = theVector.z;
    }
    /** Clone matrix. */
    static clone(theMat) {
        return [
            [theMat[0][0], theMat[0][1], theMat[0][2], theMat[0][3]],
            [theMat[1][0], theMat[1][1], theMat[1][2], theMat[1][3]],
            [theMat[2][0], theMat[2][1], theMat[2][2], theMat[2][3]],
            [theMat[3][0], theMat[3][1], theMat[3][2], theMat[3][3]]
        ];
    }
    /** Return scale matrix. */
    static scaleUnits(theMat, theScale) {
        const aResult = Mat4Tools.clone(theMat);
        aResult[0][3] *= theScale;
        aResult[1][3] *= theScale;
        aResult[2][3] *= theScale;
        return aResult;
    }
    /** Return scale matrix. */
    static scale(theVector) {
        if (typeof (theVector) === 'number') {
            return [
                [theVector, 0.0, 0.0, 0.0],
                [0.0, theVector, 0.0, 0.0],
                [0.0, 0.0, theVector, 0.0],
                [0.0, 0.0, 0.0, 1.0]
            ];
        }
        else {
            return [
                [theVector.x, 0.0, 0.0, 0.0],
                [0.0, theVector.y, 0.0, 0.0],
                [0.0, 0.0, theVector.z, 0.0],
                [0.0, 0.0, 0.0, 1.0]
            ];
        }
    }
    /** Compute matrix-array multiplication. */
    static multiplyMatrixArray(theMatA, theVecB) {
        return Mat4Tools._myViewer.mat4ToolsMultiplyArray(theMatA, theVecB);
    }
    /** Compute matrix-vector multiplication. */
    static multiplyMatrixVector(theMatA, theVecB) {
        const anArray = Array.isArray(theVecB) ? [...theVecB, 1.0]
            : [theVecB.x, theVecB.y, theVecB.z, 1.0];
        const aResultArray = Mat4Tools.multiplyMatrixArray(theMatA, anArray);
        return { x: aResultArray[0], y: aResultArray[1], z: aResultArray[2] };
    }
    /** scale part of matrix */
    static getScale(theMat) {
        const aRotMat = Mat4Tools.getRotation(theMat);
        const aRotMatInv = Mat4Tools.invert(aRotMat);
        const aMat = Mat4Tools.multiply(theMat, aRotMatInv);
        return [
            [aMat[0][0], 0.0, 0.0, 0.0],
            [0.0, aMat[1][1], 0.0, 0.0],
            [0.0, 0.0, aMat[2][2], 0.0],
            [0.0, 0.0, 0.0, 1.0]
        ];
    }
    /** rotation part of matrix */
    static getRotation(theMat) {
        const aBasisX = Vec3Tools.normalize({ x: theMat[0][0], y: theMat[1][0], z: theMat[2][0] });
        const aBasisY = Vec3Tools.normalize({ x: theMat[0][1], y: theMat[1][1], z: theMat[2][1] });
        const aBasisZ = Vec3Tools.normalize({ x: theMat[0][2], y: theMat[1][2], z: theMat[2][2] });
        return [
            [aBasisX.x, aBasisY.x, aBasisZ.x, 0.0],
            [aBasisX.y, aBasisY.y, aBasisZ.y, 0.0],
            [aBasisX.z, aBasisY.z, aBasisZ.z, 0.0],
            [0.0, 0.0, 0.0, 1.0]
        ];
    }
    /** Unscale */
    static unscale(theMat) {
        return this.multiply(this.translation(this.getTranslation(theMat)), this.getRotation(theMat));
    }
    /** Get rotation and scale part of matrix */
    static getRotationScale(theMat) {
        return this.multiply(this.getRotation(theMat), this.getScale(theMat));
    }
    /** Calculate coordinate system transformation from axes */
    static getCSTransformation(theAxes) {
        return Mat4Tools._myViewer.mat4ToolsGetCSTransformation(theAxes);
    }
    /** Calculate relative coordinate system transformation */
    static getCSRelativeTransformation(theFrom, theTo) {
        return Mat4Tools._myViewer.mat4ToolsCSRelativeTransformation(theFrom, theTo);
    }
    /** Transform coordinate system */
    static transformCS(theAxes, theMat) {
        return Mat4Tools._myViewer.mat4ToolsTransformCS(theAxes, theMat);
    }
    /** Get euler angles from matrix */
    static getEulerAngles(theMat, theOrder) {
        return Mat4Tools._myViewer.mat4ToolsGetEulerAngles(theMat, theOrder);
    }
    /** Get rotation quaternion from euler angles */
    static eulerToQuaternion(theEuler, theOrder) {
        return Mat4Tools._myViewer.mat4ToolsEulerToQuaternion(theEuler, theOrder);
    }
    /** Get rotation quaternion from euler angles */
    static quaternionToMatrix(theQuat) {
        return Mat4Tools._myViewer.mat4ToolsQuaternionToMatrix(theQuat);
    }
    /** Get quaternion from matrix */
    static getQuaternion(theMat) {
        return Mat4Tools._myViewer.mat4ToolsGetQuaternion(theMat);
    }
    /** Build rotation matrix*/
    static buildRotationMatrix(theLocation, theDirection, theAngle) {
        return Mat4Tools._myViewer.mat4ToolsBuildRotationMatrix(theLocation, theDirection, theAngle);
    }
    /** Get relative transform */
    static relativeTransform(theFrom, theTo) {
        const inverseFromTransform = this.invert(theFrom);
        const relativeTransform = Mat4Tools.multiply(theTo, inverseFromTransform);
        return relativeTransform;
    }
    /** Set Viewer */
    static setViewer(theViewer) {
        Mat4Tools._myViewer = theViewer;
    }
}
/** Return _myViewer. */
Mat4Tools._myViewer = null;
/**
 * Update [[ModelLoader]] in OccViewer.
 * @internal
 */
OccViewer.onInit((theModule) => {
    Mat4Tools.setViewer(theModule);
});
